import type { BookingProjection, FlowStepId, HotelBootstrap } from "../bridge/contract";

/**
 * Which steps this hotel actually needs, and which one to show next.
 *
 * This is the whole of the widget's "intelligence" and it is deliberately dull: a step exists
 * only when the hotel's published configuration leaves something open. A guesthouse with one
 * category and one rate never sees a category or rate step, because pressing "continue" on a
 * screen with a single option is not a choice, it is friction.
 *
 * Nothing here decides anything commercial. Whether a step is *satisfied* comes from the
 * server's blocking reasons, never from re-checking the data locally.
 */

export type StepState = "todo" | "current" | "done" | "blocked";

export type FlowStep = {
  id: FlowStepId;
  label: string;
  state: StepState;
};

const LABELS: Record<FlowStepId, string> = {
  stay: "Reisezeitraum",
  rooms: "Zimmer",
  party: "Gäste",
  category: "Zimmerkategorie",
  rate: "Rate",
  preferences: "Wünsche",
  extras: "Extras",
  contact: "Kontakt",
  review: "Prüfen",
  payment: "Zahlung",
  status: "Bestätigung",
};

/**
 * A step is shown only when the configuration leaves a real decision open.
 *
 * `rooms` is the one case that depends on state rather than configuration alone: a hotel that
 * allows several rooms still hides the step until the guest wants more than one, so a couple
 * booking one room never sees it.
 */
export function visibleSteps(bootstrap: HotelBootstrap, projection: BookingProjection): FlowStepId[] {
  const steps: FlowStepId[] = ["stay"];

  if (bootstrap.rules.maxRooms > 1) steps.push("rooms");
  steps.push("party");
  if (bootstrap.categories.length > 1) steps.push("category");
  if (bootstrap.rates.length > 1) steps.push("rate");
  if (bootstrap.preferences.length > 0 || bootstrap.requirements.length > 0) steps.push("preferences");
  if (bootstrap.addons.length > 0) steps.push("extras");
  steps.push("contact", "review");

  if (
    projection.status === "checkout_ready" ||
    projection.status === "payment_pending" ||
    projection.status === "payment_failed"
  ) steps.push("payment");
  if (
    projection.status === "paid" ||
    projection.status === "reservation_committing" ||
    projection.status === "booked"
  ) {
    steps.push("status");
  }
  return steps;
}

/** Steps that the server is currently complaining about. */
export function blockedSteps(projection: BookingProjection): Set<FlowStepId> {
  const blocked = new Set<FlowStepId>();
  for (const reason of projection.blockingReasons) {
    if (reason.severity === "blocking" && reason.step) blocked.add(reason.step);
  }
  return blocked;
}

export function buildSteps(
  bootstrap: HotelBootstrap,
  projection: BookingProjection,
  currentStep: FlowStepId,
  visited: ReadonlySet<FlowStepId> = new Set(),
): FlowStep[] {
  const visible = visibleSteps(bootstrap, projection);
  const blocked = blockedSteps(projection);
  const currentIndex = visible.indexOf(currentStep);

  return visible.map((id, index) => {
    let state: StepState = "todo";
    // A step only turns red once the guest has actually been there. On a freshly opened widget
    // every step is unresolved by definition, and marking them all as errors tells someone who
    // has not typed a character yet that they have already done something wrong.
    if (blocked.has(id) && visited.has(id) && id !== currentStep) state = "blocked";
    else if (id === currentStep) state = "current";
    else if (currentIndex >= 0 && index < currentIndex && !blocked.has(id)) state = "done";
    return { id, label: LABELS[id], state };
  });
}

/**
 * The next step the guest should land on.
 *
 * Always the earliest unresolved one, so fixing something late in the flow does not strand the
 * guest at the end with an error they have to hunt for.
 */
export function firstUnresolvedStep(
  bootstrap: HotelBootstrap,
  projection: BookingProjection,
): FlowStepId {
  const visible = visibleSteps(bootstrap, projection);
  const blocked = blockedSteps(projection);
  if (projection.status === "booked" || projection.status === "reservation_committing") return "status";
  if (
    projection.status === "checkout_ready" ||
    projection.status === "payment_pending" ||
    projection.status === "payment_failed"
  ) return "payment";
  const found = visible.find((id) => blocked.has(id));
  if (found) return found;
  return "review";
}

export function stepAfter(
  bootstrap: HotelBootstrap,
  projection: BookingProjection,
  current: FlowStepId,
): FlowStepId | null {
  const visible = visibleSteps(bootstrap, projection);
  const index = visible.indexOf(current);
  if (index < 0 || index + 1 >= visible.length) return null;
  return visible[index + 1];
}

export function stepBefore(
  bootstrap: HotelBootstrap,
  projection: BookingProjection,
  current: FlowStepId,
): FlowStepId | null {
  const visible = visibleSteps(bootstrap, projection);
  const index = visible.indexOf(current);
  if (index <= 0) return null;
  return visible[index - 1];
}

export function reasonsForStep(projection: BookingProjection, step: FlowStepId, roomId?: string) {
  return projection.blockingReasons.filter(
    (reason) => reason.step === step && (roomId === undefined || reason.roomId === null || reason.roomId === roomId),
  );
}

/**
 * Whether the guest has already tried to move past this step.
 *
 * "Please choose a room category" is not an error on the room-category step — it is the step's
 * whole purpose, and printing it in red before anyone has clicked anything treats arriving as a
 * mistake. The reason still blocks the quote; it just waits until they have gone further and
 * left the answer behind.
 */
export function hasMovedPast(
  bootstrap: HotelBootstrap,
  projection: BookingProjection,
  step: FlowStepId,
  visited: ReadonlySet<FlowStepId>,
): boolean {
  const visible = visibleSteps(bootstrap, projection);
  const index = visible.indexOf(step);
  if (index < 0) return false;
  return visible.slice(index + 1).some((later) => visited.has(later));
}

export function stepLabel(step: FlowStepId): string {
  return LABELS[step];
}
